import React from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Lightbulb,
  TrendingUp,
  XCircle,
} from "lucide-react";

/**
 * AI Assessment Card (Wave BG) — visual renderer for the B.1.4 deliverable
 * produced by simulation_specialist_v2. Shows:
 *   • Health-gauge dial (0-100 score)
 *   • Per-product spec-vs-actual cards (from product_quality[])
 *   • Parameter-suggestion cards
 *   • Design-basis warning cards
 *   • Generic flag list
 *
 * Accepts the raw AI Assessment JSON:
 *   { score, overall_verdict, product_quality[], parameter_suggestions[],
 *     design_basis_warnings[], flags[] }
 */

const VERDICT_STYLE = {
  passing: { color: "#10b981", label: "PASSING", icon: CheckCircle2 },
  attention: { color: "#f59e0b", label: "NEEDS ATTENTION", icon: AlertTriangle },
  failing: { color: "#ef4444", label: "FAILING", icon: XCircle },
  unknown: { color: "#64748b", label: "UNKNOWN", icon: Info },
};

const STATUS_STYLE = {
  "in-spec": { color: "#10b981", bg: "rgba(16,185,129,0.12)", label: "IN-SPEC" },
  "near-spec": { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", label: "NEAR SPEC" },
  "out-of-spec": { color: "#ef4444", bg: "rgba(239,68,68,0.12)", label: "OUT-OF-SPEC" },
  default: { color: "#64748b", bg: "rgba(100,116,139,0.12)", label: "UNKNOWN" },
};

function scoreColor(score) {
  if (score == null) return "#64748b";
  if (score >= 85) return "#10b981"; // green
  if (score >= 70) return "#22c55e";
  if (score >= 55) return "#f59e0b"; // amber
  if (score >= 40) return "#f97316";
  return "#ef4444"; // red
}

/** Circular SVG gauge dial — 0-100 with arc fill. */
function HealthGauge({ score }) {
  const radius = 60;
  const cx = 80;
  const cy = 80;
  const circumference = 2 * Math.PI * radius;
  const clampedScore = Math.max(0, Math.min(100, score ?? 0));
  const arcLength = (clampedScore / 100) * circumference * 0.75; // 3/4 of full
  const color = scoreColor(score);
  return (
    <div style={{ position: "relative", width: 160, height: 160, flexShrink: 0 }}
         data-testid="ai-assessment-gauge">
      <svg width={160} height={160} viewBox="0 0 160 160">
        {/* Background arc */}
        <circle
          cx={cx} cy={cy} r={radius}
          fill="none"
          stroke="var(--pf-border)"
          strokeWidth="12"
          strokeDasharray={`${circumference * 0.75} ${circumference}`}
          strokeDashoffset={circumference * 0.125}
          strokeLinecap="round"
          transform={`rotate(135 ${cx} ${cy})`}
        />
        {/* Filled arc */}
        <circle
          cx={cx} cy={cy} r={radius}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeDasharray={`${arcLength} ${circumference}`}
          strokeDashoffset={circumference * 0.125}
          strokeLinecap="round"
          transform={`rotate(135 ${cx} ${cy})`}
          style={{ transition: "stroke-dasharray 0.6s ease" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          fontSize: 38, fontWeight: 700, color,
          fontFamily: "'JetBrains Mono', monospace",
          lineHeight: 1,
        }} data-testid="ai-assessment-score">
          {score ?? "—"}
        </div>
        <div style={{ fontSize: 10, color: "var(--pf-text-muted)",
                        letterSpacing: "0.08em", marginTop: 4 }}>
          HEALTH SCORE
        </div>
      </div>
    </div>
  );
}

/** Horizontal bar for product quality — "actual vs target". */
function ProductQualityBar({ product, status, note }) {
  const st = STATUS_STYLE[status] || STATUS_STYLE.default;

  // Try to extract a numeric actual + target from the note text.
  // e.g. "99.5% purity vs 99.9% target" → actual=99.5, target=99.9
  let actual = null, target = null;
  if (typeof note === "string") {
    const m = note.match(/(\d+(?:\.\d+)?)\s*%?\s*(?:purity|yield|conv|recovery)?\s*(?:vs|to|target|\/)\s*(\d+(?:\.\d+)?)/i);
    if (m) { actual = parseFloat(m[1]); target = parseFloat(m[2]); }
  }

  const hasBar = actual !== null && target !== null && target > 0;
  const actualPct = hasBar ? Math.min(100, (actual / target) * 100) : 0;

  return (
    <div style={{
      padding: "12px 14px",
      background: "var(--pf-surface-2)",
      borderRadius: 6,
      border: "1px solid var(--pf-border)",
    }} data-testid={`ai-assessment-product-${product}`}>
      <div style={{ display: "flex", alignItems: "center",
                      justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{product}</div>
        <span style={{
          padding: "3px 8px", fontSize: 9.5, fontWeight: 700,
          letterSpacing: "0.06em", borderRadius: 4,
          color: st.color, background: st.bg, border: `1px solid ${st.color}`,
        }}>{st.label}</span>
      </div>
      {hasBar && (
        <>
          <div style={{
            display: "flex", justifyContent: "space-between",
            fontSize: 10.5, color: "var(--pf-text-muted)", marginBottom: 3,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            <span>actual: <strong style={{ color: st.color }}>{actual}</strong></span>
            <span>target: <strong style={{ color: "var(--pf-text)" }}>{target}</strong></span>
          </div>
          <div style={{
            height: 8, background: "var(--pf-surface)",
            borderRadius: 4, overflow: "hidden",
          }}>
            <div style={{
              width: `${actualPct}%`, height: "100%",
              background: st.color,
              transition: "width 0.4s ease",
            }} />
          </div>
        </>
      )}
      {note && (
        <div style={{ fontSize: 11.5, color: "var(--pf-text-muted)",
                        marginTop: hasBar ? 8 : 4 }}>
          {note}
        </div>
      )}
    </div>
  );
}

function SuggestionCard({ item, tone = "info" }) {
  const toneColor = tone === "warning" ? "#f59e0b"
                  : tone === "danger"  ? "#ef4444"
                  : "#3b82f6";
  const Icon = tone === "warning" ? AlertTriangle
             : tone === "danger"  ? XCircle
             : tone === "suggestion" ? Lightbulb
             : Info;
  const title = item.equipment || item.area || item.product || "—";
  const body = item.suggestion || item.note || item.message || JSON.stringify(item);
  return (
    <div style={{
      padding: "10px 12px",
      background: "var(--pf-surface-2)",
      borderRadius: 6,
      border: `1px solid ${toneColor}40`,
      borderLeft: `3px solid ${toneColor}`,
      display: "flex", gap: 10, alignItems: "flex-start",
    }}>
      <Icon size={16} style={{ color: toneColor, flexShrink: 0, marginTop: 2 }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 12, color: "var(--pf-text-muted)",
                        wordBreak: "break-word" }}>{body}</div>
      </div>
    </div>
  );
}

export default function AIAssessmentCard({ assessment }) {
  if (!assessment) return null;
  const {
    score, overall_verdict: verdict,
    product_quality = [], parameter_suggestions = [],
    design_basis_warnings = [], flags = [],
  } = assessment;

  const vs = VERDICT_STYLE[verdict] || VERDICT_STYLE.unknown;
  const VIcon = vs.icon;

  return (
    <div style={{
      marginTop: 12, marginBottom: 20,
      padding: 18,
      background: "var(--pf-surface)",
      border: "1px solid var(--pf-border)",
      borderRadius: 8,
    }} data-testid="ai-assessment-card">
      {/* Header: gauge + verdict */}
      <div style={{ display: "flex", gap: 20, alignItems: "center",
                      flexWrap: "wrap", marginBottom: 18 }}>
        <HealthGauge score={score} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="pf-overline"
               style={{ marginBottom: 6, color: "var(--pf-text-muted)" }}>
            AI ASSESSMENT — SIMULATION QUALITY
          </div>
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            fontSize: 22, fontWeight: 700, color: vs.color,
            marginBottom: 8,
          }} data-testid="ai-assessment-verdict">
            <VIcon size={24} /> {vs.label}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--pf-text-muted)",
                         lineHeight: 1.5 }}>
            Computed by Capexara Process Industry AI on the rigorous engine's H&MB output. Rescoring
            happens automatically on every re-run of Stage B.1.
          </div>
        </div>
      </div>

      {/* Product quality */}
      {product_quality.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div className="pf-overline" style={{ marginBottom: 8,
                   display: "flex", alignItems: "center", gap: 6 }}>
            <TrendingUp size={13} /> PRODUCT QUALITY vs DESIGN BASIS
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 10,
          }}>
            {product_quality.map((p, i) => (
              <ProductQualityBar
                key={i}
                product={p.product || p.name || `Product ${i + 1}`}
                status={p.status}
                note={p.note}
              />
            ))}
          </div>
        </div>
      )}

      {/* Parameter suggestions */}
      {parameter_suggestions.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div className="pf-overline" style={{ marginBottom: 8,
                   display: "flex", alignItems: "center", gap: 6 }}>
            <Lightbulb size={13} /> PARAMETER SUGGESTIONS
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {parameter_suggestions.map((s, i) => (
              <SuggestionCard key={i} item={s} tone="suggestion" />
            ))}
          </div>
        </div>
      )}

      {/* Design-basis warnings */}
      {design_basis_warnings.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div className="pf-overline" style={{ marginBottom: 8,
                   display: "flex", alignItems: "center", gap: 6,
                   color: "#f59e0b" }}>
            <AlertTriangle size={13} /> DESIGN-BASIS WARNINGS
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {design_basis_warnings.map((w, i) => (
              <SuggestionCard key={i}
                item={typeof w === "string" ? { note: w } : w}
                tone="warning" />
            ))}
          </div>
        </div>
      )}

      {/* Generic flags */}
      {flags.length > 0 && (
        <div>
          <div className="pf-overline" style={{ marginBottom: 8,
                   display: "flex", alignItems: "center", gap: 6 }}>
            <Info size={13} /> FLAGS
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {flags.map((f, i) => (
              <SuggestionCard key={i}
                item={typeof f === "string" ? { note: f } : f}
                tone="info" />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {product_quality.length === 0 && parameter_suggestions.length === 0 &&
       design_basis_warnings.length === 0 && flags.length === 0 && (
        <div style={{
          textAlign: "center", padding: 20,
          color: "var(--pf-text-muted)", fontSize: 12,
        }}>
          No structured assessment details available. Re-run Stage B.1 with a
          richer design basis to populate per-product and per-equipment
          suggestions.
        </div>
      )}
    </div>
  );
}
